import { z } from 'zod';
import { CustomFieldValueSchema } from './custom-field-value.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import type { GetAllParamsDto } from './types.dto';

export const demandTitleLimit = 300;

export const DemandStatusType = {
  NotStarted: 'NOT_STARTED',
  InProgress: 'IN_PROGRESS',
  Ready: 'READY',
  Done: 'DONE',
} as const;
export type DemandStatusTypeT = (typeof DemandStatusType)[keyof typeof DemandStatusType];
export const DemandStatusTypeSchema = z.nativeEnum(DemandStatusType);

export const DemandStatusSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  name: z.string().trim().min(1).max(255),
  color: z.string().max(20),
  demandStatusType: DemandStatusTypeSchema,
  sortOrder: z.number(),
  isAccountDefault: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  archivedAt: z.coerce.date().nullish(),
});
export type DemandStatusDto = z.infer<typeof DemandStatusSchema>;

export const DemandSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  ownerId: z.string().length(24),
  title: z.string().trim().min(1).max(demandTitleLimit),
  description: z.string().nullish(),
  statusId: z.number(),
  fields: z.array(CustomFieldValueSchema).default([]),
  updatedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  statusChangedAt: z.coerce.date().nullish(),
  originatingIdeaId: z.number().nullish(),
});
export type DemandDto = z.infer<typeof DemandSchema>;

export type GetAllDemandsDto = GetAllParamsDto & { statusId?: number };

// GET /api/v1/fim/demands/:id
export const GetDemandByIdResponseSchema = inferApiResultSchema(DemandSchema);
export type GetDemandByIdResponseDto = z.infer<typeof GetDemandByIdResponseSchema>;

// GET /api/v1/fim/demands
export const GetDemandsResponseSchema = inferPaginatedApiResponse(z.array(DemandSchema));
export type GetDemandsResponseDto = z.infer<typeof GetDemandsResponseSchema>;

// POST /api/v1/fim/demands
export const CreateDemandSchema = DemandSchema.omit({
  id: true,
  accountId: true,
  ownerId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  statusChangedAt: true,
}).partial({ fields: true });
export type CreateDemandDto = z.infer<typeof CreateDemandSchema>;
export const CreateDemandResponseSchema = inferApiResultSchema(DemandSchema);
export type CreateDemandResponseDto = z.infer<typeof CreateDemandResponseSchema>;

// --- Demand status management (NEX-13689) ---
// `demandStatusType` is restricted to the system-defined set above (also enforced by a DB CHECK
// constraint). Accounts may rename, recolor, reorder, add, and delete statuses, but every status
// must map to one of the four system-defined types.

export const demandStatusNameLimit = 255;
export const demandStatusColorLimit = 20;

// POST /api/v1/fim/demand-statuses
export const CreateDemandStatusSchema = z.object({
  name: z.string().trim().min(1).max(demandStatusNameLimit),
  color: z.string().trim().min(1).max(demandStatusColorLimit),
  demandStatusType: DemandStatusTypeSchema,
  isAccountDefault: z.boolean().optional(),
});
export type CreateDemandStatusDto = z.infer<typeof CreateDemandStatusSchema>;

// PUT /api/v1/fim/demand-statuses/:id
export const UpdateDemandStatusSchema = z.object({
  name: z.string().trim().min(1).max(demandStatusNameLimit).optional(),
  color: z.string().trim().min(1).max(demandStatusColorLimit).optional(),
  demandStatusType: DemandStatusTypeSchema.optional(),
  isAccountDefault: z.boolean().optional(),
});
export type UpdateDemandStatusDto = z.infer<typeof UpdateDemandStatusSchema>;

// PUT /api/v1/fim/demand-statuses/reorder
export const ReorderDemandStatusesSchema = z.object({
  orderedIds: z.array(z.number().int().positive()).min(1),
});
export type ReorderDemandStatusesDto = z.infer<typeof ReorderDemandStatusesSchema>;

// PUT /api/v1/fim/demands/:id/status
export const ChangeDemandStatusSchema = z.object({
  statusId: z.number().int().positive(),
});
export type ChangeDemandStatusDto = z.infer<typeof ChangeDemandStatusSchema>;

export const GetDemandStatusesResponseSchema = inferApiResultSchema(z.array(DemandStatusSchema));
export type GetDemandStatusesResponseDto = z.infer<typeof GetDemandStatusesResponseSchema>;

export const GetDemandStatusByIdResponseSchema = inferApiResultSchema(DemandStatusSchema);
export type GetDemandStatusByIdResponseDto = z.infer<typeof GetDemandStatusByIdResponseSchema>;

export const CreateDemandStatusResponseSchema = inferApiResultSchema(DemandStatusSchema);
export type CreateDemandStatusResponseDto = z.infer<typeof CreateDemandStatusResponseSchema>;

export const UpdateDemandStatusResponseSchema = inferApiResultSchema(DemandStatusSchema);
export type UpdateDemandStatusResponseDto = z.infer<typeof UpdateDemandStatusResponseSchema>;

export const ReorderDemandStatusesResponseSchema = inferApiResultSchema(
  z.array(DemandStatusSchema),
);
export type ReorderDemandStatusesResponseDto = z.infer<typeof ReorderDemandStatusesResponseSchema>;

export const ChangeDemandStatusResponseSchema = inferApiResultSchema(DemandSchema);
export type ChangeDemandStatusResponseDto = z.infer<typeof ChangeDemandStatusResponseSchema>;
